import { useState, useEffect } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColorAt } from '../../themes/chartThemeColors';
import { lookupService } from '../../functions/apis/lookupService';
import {
  fetchClientOverviewData,
  getTotalCostForUnitFormatted,
  getTotalCostForUnitInCurrencyFormatted,
  getTotalNetPLForUnitFormatted,
  getTotalNetPLForUnitInCurrencyFormatted,
  fetchYearsHeldForUnit,
  formatYearsHeld,
  calculateTotalAssetsUSD,
} from '../../functions/calculations';
import type { Unit, ClientOverview } from '../../types/api';
import type { LocationData } from '../overview/types';

export interface UnitMetricsState {
  unitCurrency: string;
  totalCost: string;
  totalNetPL: string;
  yearsHeld: string;
  metricsLoading: boolean;
  ttmROI: number;
  portfolioPercentage: number;
  ownership: number;
  mapLocations: LocationData[];
  totalCostTrend: { value: number; type: 'positive' | 'negative' };
  totalNetPLTrend: { value: number; type: 'positive' | 'negative' };
}

export function useUnitMetrics() {
  const { theme } = useTheme();
  const { selectedClient, selectedUnit, clientUnits, currencies } = useFilter();
  const [unitCurrency, setUnitCurrency] = useState('USD');
  const [unitDetails, setUnitDetails] = useState<Unit | null>(null);
  const [unitDetailsLoading, setUnitDetailsLoading] = useState(false);
  const [metrics, setMetrics] = useState<Omit<UnitMetricsState, 'unitCurrency'>>({
    totalCost: '0',
    totalNetPL: '0',
    yearsHeld: '0 years',
    metricsLoading: false,
    ttmROI: 0,
    portfolioPercentage: 0,
    ownership: 0,
    mapLocations: [],
    totalCostTrend: { value: 13, type: 'positive' },
    totalNetPLTrend: { value: 13, type: 'positive' },
  });

  useEffect(() => {
    const fetchUnitDetails = async () => {
      if (!selectedUnit?.unit_id_no) {
        setUnitDetails(null);
        setUnitCurrency('USD');
        return;
      }

      setUnitDetailsLoading(true);
      try {
        const units = await lookupService.getUnits().catch(() => [] as Unit[]);
        const unit = units.find((u) => u.unit_id_no === selectedUnit.unit_id_no);
        setUnitDetails(unit || null);
        setUnitCurrency(unit?.currency || 'USD');
      } catch (error) {
        console.error('Error fetching unit details:', error);
        setUnitDetails(null);
        setUnitCurrency('USD');
      } finally {
        setUnitDetailsLoading(false);
      }
    };

    fetchUnitDetails();
  }, [selectedUnit]);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!selectedClient?.user_id_no || !selectedUnit?.unit_id_no) {
        setMetrics((prev) => ({
          ...prev,
          totalCost: '0',
          totalNetPL: '0',
          yearsHeld: '0 years',
          ttmROI: 0,
          ownership: 0,
          portfolioPercentage: 0,
          mapLocations: [],
        }));
        return;
      }

      setMetrics((prev) => ({ ...prev, metricsLoading: true }));

      try {
        const [selectedUnitOverviewData, allClientOverviewData, allUnits, years] = await Promise.all([
          fetchClientOverviewData(selectedClient.user_id_no, [selectedUnit]),
          clientUnits?.length
            ? fetchClientOverviewData(selectedClient.user_id_no, clientUnits)
            : Promise.resolve([]),
          lookupService.getUnits().catch(() => [] as Unit[]),
          fetchYearsHeldForUnit(selectedClient.user_id_no, selectedUnit.unit_id_no),
        ]);

        const unitOverview = selectedUnitOverviewData.find(
          (overview: ClientOverview) => overview.unit_id_no === selectedUnit.unit_id_no
        );

        const needsConversion = unitCurrency !== 'USD' && currencies.length > 0;

        let ttmROI = 0;
        let ownership = 0;
        let portfolioPercentage = 0;

        if (unitOverview) {
          const roiValue = unitOverview.ttm_roi_percent || 0;
          ttmROI = roiValue > 1 ? roiValue : roiValue * 100;
          ownership = (unitOverview.percent_ownership || 0) * 100;
          const unitAssets = unitOverview.total_assets_usd || 0;
          const totalClientAssets = allClientOverviewData.length
            ? calculateTotalAssetsUSD(allClientOverviewData)
            : calculateTotalAssetsUSD(selectedUnitOverviewData);
          portfolioPercentage = totalClientAssets > 0 ? (unitAssets / totalClientAssets) * 100 : 0;
        }

        const unit = allUnits.find((u) => u.unit_id_no === selectedUnit.unit_id_no);
        const mapLocations: LocationData[] =
          unit?.latitude && unit?.longitude
            ? [{
                id: `unit-${selectedUnit.unit_id_no}`,
                lat: unit.latitude,
                lng: unit.longitude,
                value: unitOverview?.total_assets_usd || 0,
                label: `${unit.city || ''}${unit.city && unit.country ? ', ' : ''}${unit.country || `Unit ${selectedUnit.unit_id_no}`}`.trim(),
                color: getChartColorAt(theme, selectedUnit.unit_id_no),
              }]
            : [];

        setMetrics({
          totalCost: needsConversion
            ? getTotalCostForUnitInCurrencyFormatted(selectedUnitOverviewData, selectedUnit.unit_id_no, unitCurrency, currencies)
            : getTotalCostForUnitFormatted(selectedUnitOverviewData, selectedUnit.unit_id_no),
          totalNetPL: needsConversion
            ? getTotalNetPLForUnitInCurrencyFormatted(selectedUnitOverviewData, selectedUnit.unit_id_no, unitCurrency, currencies)
            : getTotalNetPLForUnitFormatted(selectedUnitOverviewData, selectedUnit.unit_id_no),
          yearsHeld: formatYearsHeld(years),
          metricsLoading: false,
          ttmROI,
          portfolioPercentage,
          ownership,
          mapLocations,
          totalCostTrend: { value: 13, type: 'positive' },
          totalNetPLTrend: { value: 13, type: 'positive' },
        });
      } catch (error) {
        console.error('Error fetching unit metrics:', error);
        setMetrics((prev) => ({
          ...prev,
          totalCost: '0',
          totalNetPL: '0',
          yearsHeld: '0 years',
          metricsLoading: false,
          ttmROI: 0,
          ownership: 0,
          portfolioPercentage: 0,
          mapLocations: [],
        }));
      }
    };

    fetchMetrics();
  }, [selectedClient, selectedUnit, unitCurrency, currencies, clientUnits]);

  useEffect(() => {
    setMetrics((prev) => {
      if (prev.mapLocations.length === 0) return prev;

      return {
        ...prev,
        mapLocations: prev.mapLocations.map((location) => {
          const unitId = Number(String(location.id).replace('unit-', ''));
          return {
            ...location,
            color: Number.isFinite(unitId) ? getChartColorAt(theme, unitId) : location.color,
          };
        }),
      };
    });
  }, [theme]);

  return {
    unitCurrency,
    unitDetails,
    unitDetailsLoading,
    ...metrics,
  };
}
